<script setup lang="ts">
import { useIsFetching } from '@tanstack/vue-query'
import type { StudioViews } from "~/types/studio";
/**
 * Space 2 — the organisation. Same shell as the project (216px sidebar, 44px topbar),
 * the sidebar lists the organisation's sections instead of the project's. The third
 * column is each page's InspectorPane, so every organisation view is list + pane like
 * every project view. Below lg the sidebar is a drawer opened from the topbar — the same
 * behaviour as layouts/portal.vue; seven equal sections need no tab bar.
 */
import { computed, ref, watch } from 'vue'
import SidebarItem from '~/components/layout/SidebarItem.vue'
import ActivityIndicator from '~/components/layout/ActivityIndicator.vue'
import UserAvatarMenu from '~/components/layout/UserAvatarMenu.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import Icon from '~/components/ui/Icon.vue'
const fetching = useIsFetching({ queryKey: ['studio'] })

const session = useSession()
watch(session, (value) => {
  if (value.isPending) return
  if (!value.data) void navigateTo('/login')
  else if (!value.data.session.activeOrganizationId) void navigateTo('/onboarding')
}, { immediate: true })
const readError = useState<string | null>('studio-read-error', () => null)
const route = useRoute()
const { data: nav } = useStudioData<StudioViews["/api/org-nav"]>('/api/org-nav')
provide('studio-org-name', computed(() => nav.value?.org ?? 'Organisation'))
const base = computed(() => '/org/' + (nav.value?.slug ?? ''))
const active = (path: string) => route.path === base.value + path || (path !== '' && route.path.startsWith(base.value + path + '/'))

const actions = useStudioActions()
const { currentProject, switchProject } = useProjectContext()
/* `/org/current/...` resolves to the organisation's own slug. The whole address is carried over,
   query and all: a page reached with a parameter — the way back to the deployment it was opened
   from, for one — must not lose it on the way in (founder, 2026-09-21, note 44). */
watch(() => nav.value?.slug, (slug) => { if(slug && route.params.slug && route.params.slug !== slug) void navigateTo(route.fullPath.replace('/org/' + route.params.slug, '/org/' + slug), {replace:true}) })
const drawer = ref(false)
watch(() => route.path, () => { drawer.value = false; readError.value = null })
</script>

<template>
  <div class="flex h-screen bg-background text-foreground">
    <button v-if="drawer" type="button" class="fixed inset-0 z-30 bg-background/70 lg:hidden" aria-label="Close menu" @click="drawer = false" />

    <aside
class="z-40 flex w-[var(--shell-sidebar-width)] shrink-0 flex-col border-r border-sidebar-border bg-sidebar max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:transition-transform"
           :class="drawer ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full'">
      <div class="flex h-[var(--shell-topbar-height)] shrink-0 items-center gap-2 border-b border-sidebar-border px-3">
        <span class="grid size-5 place-items-center rounded-[var(--radius-control)] bg-gradient-primary text-[length:var(--type-meta)] font-bold text-primary-foreground">S</span>
        <span class="text-[length:var(--type-control)] font-bold tracking-[-0.01em]">Scalty</span>
        <span class="num rounded-[var(--radius-label)] border border-border px-1 py-0.5 text-[length:var(--type-badge)] text-muted-foreground">AI STUDIO</span>
        <button type="button" class="ml-auto text-muted-foreground hover:text-foreground lg:hidden" aria-label="Close menu" @click="drawer = false">
          <Icon name="x" :size="13" />
        </button>
      </div>

      <div class="shrink-0 border-b border-sidebar-border p-2">
        <ActionButton block size="lg" label="Back to project" @click="navigateTo('/overview')">
          <template #icon><Icon name="arrow-right" :size="14" class="rotate-180" /></template>
        </ActionButton>
        <p v-if="currentProject" class="mb-0 mt-1.5 truncate px-1 text-[length:var(--type-meta)] text-muted-foreground" :title="currentProject.name">{{ currentProject.name }}</p>
      </div>

      <nav class="flex min-h-0 flex-col gap-px overflow-y-auto p-1.5">
        <div class="flex h-[var(--toolbar-control-height)] items-center px-2 text-[length:var(--type-meta)] font-semibold text-sidebar-foreground/45">Organisation</div>
        <SidebarItem label="Overview" :to="base" :active="active('')"><template #icon><Icon name="overview" :size="15" /></template></SidebarItem>
        <SidebarItem label="People" :to="base + '/people'" :active="active('/people')" :count="nav?.people.count" :tone="nav?.people.tone"><template #icon><Icon name="people" :size="15" /></template></SidebarItem>
        <SidebarItem label="Billing" :to="base + '/billing'" :active="active('/billing')" :count="nav?.billing.count" :tone="nav?.billing.tone"><template #icon><Icon name="billing" :size="15" /></template></SidebarItem>
        <SidebarItem label="Usage" :to="base + '/usage'" :active="active('/usage')"><template #icon><Icon name="usage" :size="15" /></template></SidebarItem>
        <SidebarItem label="API keys" :to="base + '/keys'" :active="active('/keys')"><template #icon><Icon name="key" :size="15" /></template></SidebarItem>
        <SidebarItem label="Connectors" :to="base + '/connectors'" :active="active('/connectors')" :count="nav?.connectors.count" :tone="nav?.connectors.tone"><template #icon><Icon name="connector" :size="15" /></template></SidebarItem>
        <SidebarItem label="Projects" :to="base + '/projects'" :active="active('/projects')" :count="nav?.projects.length"><template #icon><Icon name="folder" :size="15" /></template></SidebarItem>
        <SidebarItem label="Audit" :to="base + '/audit'" :active="active('/audit')"><template #icon><Icon name="audit" :size="15" /></template></SidebarItem>

        <div class="mt-3 flex h-[var(--toolbar-control-height)] items-center px-2 text-[length:var(--type-meta)] font-semibold text-sidebar-foreground/45">Projects · {{ nav?.projects.length ?? 0 }}</div>
        <SidebarItem v-for="p in nav?.projects" :key="p.id" :label="p.name" :to="'/overview'" :count="p.running" :tone="p.running ? 'progress' : undefined" @click="switchProject(p.id)">
          <template #icon><Icon name="folder" :size="15" /></template>
        </SidebarItem>
      </nav>

      <span class="mt-auto" />
      <UserAvatarMenu />
    </aside>

    <div class="flex min-w-0 flex-1 flex-col">
      <header class="flex h-[var(--shell-topbar-height)] shrink-0 items-center gap-2.5 border-b border-border bg-card/60 px-3">
        <button
type="button" class="grid size-7 shrink-0 place-items-center rounded-[var(--radius-control)] text-muted-foreground hover:bg-muted lg:hidden"
                aria-label="Open menu" @click="drawer = true">
          <Icon name="list" :size="15" />
        </button>
        <div class="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-xs text-muted-foreground">
          <span class="truncate">{{ nav?.org }}</span><span>/</span>
          <span class="font-semibold text-foreground">{{ route.meta.crumb ?? 'Overview' }}</span>
        </div>
        <span class="flex-1" />
        <button type="button" class="hidden h-7 w-[var(--measure-15p7143rem)] shrink-0 items-center gap-2 whitespace-nowrap rounded-[var(--radius-control)] border border-border bg-muted/50 px-2 text-xs text-muted-foreground md:flex" @click="actions.open('search')">
          <Icon name="search" :size="13" /><span class="flex-1 text-left">Search or jump to…</span>
          <kbd class="num rounded-[var(--radius-label)] border border-border px-1 py-0.5 text-[length:var(--type-fine)]">⌘K</kbd>
        </button>
        <button type="button" class="grid size-7 shrink-0 place-items-center rounded-[var(--radius-control)] text-muted-foreground hover:bg-muted md:hidden" aria-label="Search" @click="actions.open('search')">
          <Icon name="search" :size="15" />
        </button>
        <ActivityIndicator :running="nav?.running ?? null" />
        <button type="button" class="grid size-7 place-items-center rounded-[var(--radius-control)] text-muted-foreground hover:bg-muted"><Icon name="bell" :size="15" /></button>
        <button type="button" class="hidden h-7 rounded-[var(--radius-control)] border border-border px-2.5 text-xs font-medium hover:bg-muted md:block" @click="navigateTo('/api-reference')">API</button>
      </header>

      <main :aria-busy="fetching > 0" class="scrollbar-none min-h-0 flex-1 overflow-auto px-5 py-4 max-lg:px-3">
        <div v-if="readError" role="alert" class="mb-3 rounded-lg border border-destructive/30 bg-card px-3 py-2 text-xs text-destructive">{{ readError }} <button type="button" class="ml-2 underline" @click="readError = null">Dismiss</button></div>
        <slot />
      </main>
    </div>
  </div>
</template>
